import { router, Link } from '@inertiajs/react'
import { Plus, Pencil } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import { useOrgPath } from '@/lib/pathFor'
import { capitalize } from '@/lib/utils'

interface UserItem {
  id: string
  name: string | null
  email_address: string
  role: 'admin' | 'operator'
  status: 'active' | 'deactivated'
  created_at: string
  last_active_at: string | null
}

interface Props {
  users: UserItem[]
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function UsersIndex({ users }: Props) {
  const pathFor = useOrgPath()
  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <h1 className="text-sm font-semibold">Users</h1>
        <Button size="sm" className="text-xs" onClick={() => router.visit(pathFor('/settings/users/new'))}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add User
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/30 text-left">
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Name</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Email</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Role</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Last Active</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b hover:bg-muted/30">
                <td className="px-6 py-3 text-[13px] font-medium text-foreground">
                  {user.name || <span className="italic text-muted-foreground">No name</span>}
                </td>
                <td className="px-6 py-3 text-[13px] text-foreground">{user.email_address}</td>
                <td className="px-6 py-3">
                  <Badge variant="secondary" size="sm">
                    {capitalize(user.role)}
                  </Badge>
                </td>
                <td className="px-6 py-3">
                  <Badge
                    variant="secondary"
                    size="sm"
                    className={`font-medium ${
                      user.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {capitalize(user.status)}
                  </Badge>
                </td>
                <td className="px-6 py-3 text-[13px] text-muted-foreground">
                  {formatDate(user.last_active_at)}
                </td>
                <td className="px-6 py-3 text-right">
                  <Link
                    href={pathFor(`/settings/users/${user.id}/edit`)}
                    className="text-muted-foreground hover:text-foreground cursor-pointer"
                    title="Edit user"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Link>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-sm text-muted-foreground">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

UsersIndex.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
